/**
 * Where the browser leg hands the identity back to the app.
 *
 * One definition, imported by both halves: the mint route
 * (/api/auth/native/handoff) builds this URL, and native-auth-bridge.tsx
 * matches it. Two string literals in two files would be a silent, total
 * failure of native sign-in the moment one of them moved.
 *
 * ## Why an https URL and not a custom scheme
 *
 * The return leg used to be `rakemori://auth?code=…`. Any app on the device
 * can register a custom scheme, and the flow-start route cannot be
 * session-gated — sign-in has not happened when it runs — so a hostile app
 * could supply its own challenge, have the victim's browser mint a code
 * against it, receive the redirect, and redeem it with the verifier it already
 * held. Full account takeover with no user interaction.
 *
 * An https URL on our own domain is claimable only by an app the platform has
 * verified against `https://rakemori.com/.well-known/assetlinks.json` (Android)
 * or the `applinks:` entitlement (iOS), so the redirect reaches the real app or
 * it reaches a browser — never a third-party app. That is what closes the hole;
 * the PKCE verifier below it is defence in depth rather than the only barrier.
 * PKCE does NOT close it on its own: in the attack above the hostile app
 * *starts* the flow, so the verifier binding the code is the one it generated.
 *
 * ## Why the custom scheme is nonetheless back, for iOS only
 *
 * `com.apple.developer.associated-domains` cannot be issued to a free Apple ID,
 * and the AltStore IPA ships unsigned for the user's own profile to re-sign, so
 * on a sideloaded install the entitlement never arrives and the https return
 * lands in Safari instead of the app. Google sign-in cannot complete there at
 * all — see docs/store/altstore-checklist.md.
 *
 * The scheme is reachable ONLY from src/app/native/auth/page.tsx, and only when
 * that page has actually been rendered — which on an entitled build never
 * happens, because iOS intercepts the https redirect before a browser sees it.
 * The mint route still emits the https URL and nothing else. That ordering is
 * the containment: an App Store install keeps the verified-app guarantee
 * untouched, and the window above is open only on installs whose signing has
 * already given it up. It is a real reduction in security for those installs,
 * accepted deliberately so that sideloaded users can sign in with Google, and
 * closed for good by a paid Apple Developer Program membership.
 *
 * ## Why this path serves a real page
 *
 * App Link verification can fail (a reinstall that could not reach the
 * assetlinks file, a device with the feature disabled). The link then opens in
 * the browser, so src/app/native/auth/page.tsx exists to explain that rather
 * than 404. It checks the `code` param for presence only — to redirect it out
 * of the URL before any client script runs — and never reads its value.
 */
export const NATIVE_AUTH_RETURN_PATH = "/native/auth";

/**
 * Whether a deep link the app was opened with is the sign-in return.
 *
 * Matched on the PARSED origin and pathname, never on a raw prefix.
 * `startsWith` on a whole URL is the wrong shape for an https link: it makes
 * the match sensitive to the query string and to how the host happens to be
 * spelled, so a longer lookalike origin would satisfy it while the genuine
 * link with an extra parameter in front might not. Parsing first and
 * comparing components removes both.
 *
 * The origin IS checked here, and that is load-bearing rather than
 * redundant. Android's domain verification only governs *implicit* intents —
 * a link tapped in another app or the system browser. MainActivity is
 * exported (it has to be, to receive those links at all), so any other app
 * installed on the device can start it directly with an *explicit* intent
 * carrying an arbitrary `data` URI — a path verification never gets a say
 * in. Without an origin check, a pathname-only match would accept
 * `https://evil.example/native/auth?code=…`, a URL with credentials embedded
 * in the authority section to disguise its real host (e.g.
 * `https://rakemori.com:x@evil.example/native/auth`), or the app's own host
 * on the wrong port — and treat every one of them as the genuine return: the
 * injected link consumes the stored verifier, marks the deep link handled,
 * and closes the tab, killing a real in-flight sign-in for no payoff beyond
 * denial of service. Comparing the full parsed origin closes all of that at
 * once, since `URL#origin` is derived from scheme, host, and port only —
 * userinfo tricks and lookalike substrings cannot move it.
 *
 * `expectedOrigin` is passed in by the caller — ordinarily
 * `window.location.origin`, the same source src/app/login/login-form.tsx
 * uses to build the flow-start URL — rather than hardcoded to
 * `https://rakemori.com`, so localhost and preview deploys keep matching
 * their own origin instead of being pinned to production's.
 *
 * A trailing slash on the path is tolerated because it is a normalization
 * detail of whatever redirected here, not a different destination.
 */
/**
 * The iOS-only fallback return, handed over by src/app/native/auth/page.tsx.
 *
 * Split into scheme and host so the matcher below compares parsed components
 * rather than a prefix: `rakemori:` is ours, but `rakemorix:` and
 * `rakemori-app:` are not, and a `startsWith` on the whole URL would not tell
 * them apart.
 */
export const NATIVE_AUTH_SCHEME = "rakemori:";
export const NATIVE_AUTH_SCHEME_HOST = "auth";

/** `rakemori://auth?code=…`, with the code percent-encoded by URLSearchParams. */
export function nativeAuthSchemeUrl(code: string): string {
  const url = new URL(`${NATIVE_AUTH_SCHEME}//${NATIVE_AUTH_SCHEME_HOST}`);
  url.searchParams.set("code", code);
  return url.toString();
}

export function isNativeAuthReturnUrl(url: string, expectedOrigin: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Not an absolute URL at all — an intent the app was opened with that has
    // nothing to do with sign-in.
    return false;
  }

  // Checked before the origin comparison because it cannot survive one:
  // `URL#origin` is the literal string "null" for every non-special scheme, so
  // a scheme URL would fail the https branch no matter which origin was passed.
  // The host carries the whole match instead, which is why it is compared
  // exactly and why a path below it is refused rather than ignored.
  if (parsed.protocol === NATIVE_AUTH_SCHEME) {
    return parsed.host === NATIVE_AUTH_SCHEME_HOST && parsed.pathname.replace(/\/+$/, "") === "";
  }

  if (parsed.origin !== expectedOrigin) return false;
  return parsed.pathname.replace(/\/+$/, "") === NATIVE_AUTH_RETURN_PATH;
}
